import type React from "react";
import { Box, Typography, TextField, Divider } from "@mui/material";
import type { Client } from "../types/proposal.types";
import type { Booking } from "../types/booking.types";
import DeceasedSearch from "./DeceasedSearch";

interface ClientInformationSectionProps {
  selectedClient: Client | null;
  onClientSelect: (client: Client | null) => void;
  deceasedName: string;
  eventTitle: string;
  bookingType: "FUNERAL" | "EVENT" | "CONFERENCE";
  onDeceasedNameChange: (name: string) => void;
  onEventTitleChange: (name: string) => void;
  onDeceasedBirthDateChange: (date: string) => void;
  onDeceasedPassedDateChange: (date: string) => void;
  onServiceVenueChange?: (venue: string) => void;
  onServiceDateChange?: (date: string) => void;
  preSelectedBooking: Booking | null;
  onBookingIdChange?: (bookingId: number | null) => void;
  onBookingTypeChange?: (type: "FUNERAL" | "EVENT" | "CONFERENCE") => void;
}

const ClientInformationSection: React.FC<ClientInformationSectionProps> = ({
  selectedClient,
  onClientSelect,
  deceasedName,
  onDeceasedNameChange,
  onDeceasedBirthDateChange,
  onDeceasedPassedDateChange,
  onServiceVenueChange,
  onServiceDateChange,
  preSelectedBooking,
  eventTitle,
  onEventTitleChange,
  bookingType: _bookingType,
  onBookingIdChange,
  onBookingTypeChange
}) => {
  void _bookingType;
  const handleBookingSelect = (booking: Booking | null) => {
    if (!booking) {
      onClientSelect(null);
      onDeceasedNameChange("");
      onDeceasedBirthDateChange("");
      onDeceasedPassedDateChange("");
      if (onServiceVenueChange) onServiceVenueChange("");
      if (onServiceDateChange) onServiceDateChange("");
      if (onBookingIdChange) onBookingIdChange(null);
      return;
    }

    if (onBookingIdChange) onBookingIdChange(booking.id);

    const contact = booking.contact;
    const client: Client = {
      id: contact.id.toString(),
      name: `${contact.first_name} ${contact.last_name}`,
      email: contact.email || "",
      phone: contact.phone || "",
      address: [contact.address_one, contact.city, contact.country]
        .filter(Boolean)
        .join(", "),
    };
    onClientSelect(client);

    if (booking.booking_type?.toUpperCase() === "CONFERENCE") {
      if (onBookingTypeChange) onBookingTypeChange("CONFERENCE");
    } else if (booking.deceased && booking.deceased.length > 0) {
      if (onBookingTypeChange) onBookingTypeChange("FUNERAL");
    } else if (booking.events && booking.events.length > 0) {
      if (onBookingTypeChange) onBookingTypeChange("EVENT");
    }

    if (booking.deceased && booking.deceased.length > 0) {
      const deceased = booking.deceased[0];
      const fullName = [
        deceased.first_name,
        deceased.second_name,
        deceased.surname,
      ]
        .filter(Boolean)
        .join(" ");
      onDeceasedNameChange(fullName);
      onDeceasedBirthDateChange(deceased.date_of_birth);
      onDeceasedPassedDateChange(deceased.date_of_death);
    } else if (booking.events && booking.events.length > 0) {
      const event = booking.events[0];
      onEventTitleChange(event.title);
      onDeceasedNameChange("");
      onDeceasedBirthDateChange("");
      onDeceasedPassedDateChange("");
    }

    if (booking.services && booking.services.length > 0) {
      const service = booking.services[0];
      if (onServiceVenueChange && service.venue) {
        const venueInfo = `${service.venue.name}, ${service.venue.city}`;
        onServiceVenueChange(venueInfo);
      }
      if (onServiceDateChange) {
        onServiceDateChange(service.date_of_service);
      }
    }
  };

  const isEventBooking =
    !!preSelectedBooking?.events?.length &&
    !preSelectedBooking?.deceased?.length;
  const isDeceasedBooking = !!preSelectedBooking?.deceased?.length;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Client Information
      </Typography>

      {!preSelectedBooking && !deceasedName && !eventTitle && (
        <Box sx={{ mb: 2 }}>
          <DeceasedSearch onBookingSelect={handleBookingSelect} />
        </Box>
      )}

      {selectedClient && (
        <Box sx={{ mb: 2, p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Client:</strong> {selectedClient.name}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Email:</strong> {selectedClient.email}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Phone:</strong> {selectedClient.phone}
          </Typography>
          <Typography variant="body2">
            <strong>Address:</strong> {selectedClient.address}
          </Typography>
        </Box>
      )}

      {(deceasedName || eventTitle || preSelectedBooking) && (
        <>
          <Divider sx={{ my: 2 }} />
          <TextField
            fullWidth
            variant="standard"
            label={
              isDeceasedBooking
                ? "Deceased Name"
                : isEventBooking
                ? "Event Title"
                : "Name"
            }
            value={isEventBooking ? eventTitle : deceasedName}
            onChange={(e) =>
              isEventBooking
                ? onEventTitleChange(e.target.value)
                : onDeceasedNameChange(e.target.value)
            }
            sx={{ mb: 2 }}
            placeholder={
              isDeceasedBooking
                ? "Deceased name"
                : isEventBooking
                ? "Event title"
                : "Name"
            }
            InputProps={{
              readOnly: !!preSelectedBooking,
            }}
          />

          {isDeceasedBooking && (
            <>
              <TextField
                fullWidth
                variant="standard"
                label="Date of Birth"
                value={preSelectedBooking?.deceased?.[0]?.date_of_birth || ""}
                onChange={(e) => onDeceasedBirthDateChange(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="YYYY-MM-DD"
                InputProps={{
                  readOnly: !!preSelectedBooking,
                }}
              />

              <TextField
                fullWidth
                variant="standard"
                label="Date of Death"
                value={preSelectedBooking?.deceased?.[0]?.date_of_death || ""}
                onChange={(e) => onDeceasedPassedDateChange(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="YYYY-MM-DD"
                InputProps={{
                  readOnly: !!preSelectedBooking,
                }}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default ClientInformationSection;
